import type {
  DeviceDetail,
  DeviceSummary,
  ExtractionRow,
  FleetSettings,
  Operator,
  OverviewPayload,
  RunRow,
  StepRecord,
  TaskRow,
} from "@/types/fleet";
import { emptySchedule } from "@/types/fleet";

/**
 * An in-memory fleet, shaped exactly like the wire.
 *
 * The point is not a demo — it is that the console can be built and reviewed
 * while the server is being written in parallel, and that the awkward states
 * (a device that has never checked in, a run that failed mid-step, a schedule
 * with a heavy fan-out) are present by construction rather than only turning up
 * in production.
 */

const NOW = Date.now();
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const iso = (ms: number) => new Date(ms).toISOString();

export const MOCK_OPERATOR: Operator = {
  id: "op_01JQZX3M7K",
  username: "tony",
  role: "admin",
  permissions: [
    "read",
    "dispatch_run",
    "stop_run",
    "remote_control",
    "edit_task",
    "manage_device",
    "enroll_device",
    "export_data",
    "danger_zone",
  ],
};

/* ------------------------------------------------------------------ *
 * Devices
 * ------------------------------------------------------------------ */

const TAGS = [["hk-a"], ["hk-a", "pilot"], ["hk-b"], ["hk-b", "night"], ["lab"], []];
const MODELS = ["SM-A336E", "SM-A546E", "Pixel 7a", "Redmi Note 12"];
const VERSIONS = ["0.2.0", "0.2.0", "0.2.0", "0.1.9"];

function makeDevice(i: number): DeviceSummary {
  // A realistic spread: mostly online, a handful stale, a few long gone, one
  // enrolled but never seen.
  let lastSeenMs: number | null;
  let online: boolean;
  let health: DeviceSummary["health"];

  if (i === 13) {
    lastSeenMs = null;
    online = false;
    health = "unseen";
  } else if (i % 17 === 3) {
    lastSeenMs = NOW - (2 + (i % 5)) * DAY;
    online = false;
    health = "offline";
  } else if (i % 11 === 5) {
    lastSeenMs = NOW - (2 + (i % 9)) * HOUR;
    online = false;
    health = "stale";
  } else {
    lastSeenMs = NOW - (i % 4) * MIN;
    online = true;
    health = "online";
  }

  const running = online && i % 9 === 2;

  return {
    id: `dev_01J${String(i).padStart(3, "0")}QZX3M7KA${i}`,
    label: `HK-${String(i + 1).padStart(2, "0")}`,
    model: MODELS[i % MODELS.length],
    android_sdk: 33 + (i % 3),
    app_version_name: VERSIONS[i % VERSIONS.length],
    app_version_code: VERSIONS[i % VERSIONS.length] === "0.2.0" ? 2 : 1,
    group_id: i % 3 === 0 ? "grp_01JHKA" : "grp_01JHKB",
    tags: TAGS[i % TAGS.length],
    online,
    last_seen_at: lastSeenMs === null ? null : iso(lastSeenMs),
    health,
    battery_pct: online ? 30 + ((i * 7) % 70) : i % 5 === 0 ? 11 : 62,
    charging: i % 5 === 0,
    screen_on: running || i % 6 === 0,
    keyguard_locked: !running && i % 3 !== 0,
    a11y_connected: i % 19 !== 7,
    disarmed: i === 21,
    revoked: false,
    active_run: running
      ? {
          id: `run_live${String(i).padStart(3, "0")}`,
          task_name: "每日天氣記錄",
          n: 4 + (i % 20),
          max_steps: 40,
        }
      : null,
    today: { success: online ? 3 + (i % 6) : 0, failed: i % 7 === 4 ? 1 : 0 },
    control: { enabled: i === 8, session_id: i === 8 ? "s_01JCTRL" : null, expires_at: null },
    config_version: 7,
    task_set_version: 3,
    pending_commands: online ? 0 : i % 4 === 1 ? 2 : 0,
  };
}

export const MOCK_DEVICES: DeviceSummary[] = Array.from({ length: 24 }, (_, i) => makeDevice(i));

export function mockDeviceDetail(id: string): DeviceDetail | null {
  const base = MOCK_DEVICES.find((d) => d.id === id);
  if (!base) return null;
  return {
    ...base,
    manufacturer: base.model.startsWith("SM") ? "Samsung" : base.model.startsWith("Pixel") ? "Google" : "Xiaomi",
    notes: base.label === "HK-01" ? "門市前台專用機，勿移動。" : null,
    enrolled_at: iso(NOW - 46 * DAY),
    battery_temp_c: 30.5,
    power_source: base.charging ? "ac" : "battery",
    ime_enabled: true,
    shizuku: "NOT_INSTALLED",
    free_storage_bytes: 12_884_901_888,
    network_type: "WIFI",
    uptime_ms: 3 * DAY + 4 * HOUR,
    pending_outbox: base.pending_commands,
    last_run: {
      id: "run_01JPREV0001",
      status: base.today.failed > 0 ? "FAILED" : "SUCCESS",
      finished_at: NOW - 3 * HOUR,
    },
    settings: {
      baseUrl: "https://api.agens.io/v1",
      modelId: "Agens Pilot",
      defaultVisionMode: "AUTO",
      defaultThinkingPolicy: "ADAPTIVE",
      defaultMaxSteps: 40,
      compactionThresholdTokens: 300000,
      keepScreenshotsDays: 7,
      unlockGraceMinutes: 120,
      webhookUrl: "https://hooks.example.com/infocollect",
      webhookSecret: "••••••••",
    },
    managed: [
      "baseUrl",
      "modelId",
      "defaultVisionMode",
      "defaultThinkingPolicy",
      "defaultMaxSteps",
      "compactionThresholdTokens",
      "keepScreenshotsDays",
      "unlockGraceMinutes",
      "webhookUrl",
      "webhookSecret",
    ],
    device_local: {
      deviceMode: "DEDICATED",
      imeFallback: false,
      shizukuMode: "OFF",
      onboardingDone: true,
      fleetDisarmed: base.disarmed,
      fleetOffline: false,
    },
    assigned_tasks: [
      {
        task_id: "tsk_01JWEATHER",
        task_name: "每日天氣記錄",
        enabled: true,
        next_fire_at: iso(NOW + 2 * HOUR),
      },
      {
        task_id: "tsk_01JWHATSAPP",
        task_name: "WhatsApp 未讀訊息摘要",
        enabled: false,
        next_fire_at: null,
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Tasks
 * ------------------------------------------------------------------ */

export const MOCK_TASKS: TaskRow[] = [
  {
    id: "tsk_01JWEATHER",
    name: "每日天氣記錄",
    goal: "打開天氣 App，記錄今日最高及最低氣溫，然後用 extract 交回。",
    allowedPackages: ["com.google.android.apps.weather", "com.android.chrome"],
    blockedLabelsRegex: null,
    allowSend: false,
    extractionSchema: JSON.stringify(
      {
        type: "object",
        properties: {
          temp_high: { type: "number" },
          temp_low: { type: "number" },
          condition: { type: "string" },
        },
        required: ["temp_high", "temp_low"],
      },
      null,
      2,
    ),
    schedule: { type: "DAILY", timesOfDay: ["08:00", "20:00"], daysOfWeek: [], intervalMinutes: null },
    visionMode: "AUTO",
    thinkingPolicy: "ADAPTIVE",
    maxSteps: 40,
    runTimeoutMinutes: 15,
    webhookUrl: null,
    requireUnlock: true,
    enabled: true,
    createdAt: NOW - 40 * DAY,
    updatedAt: NOW - 2 * DAY,
    version: 4,
    created_at: iso(NOW - 40 * DAY),
    updated_at: iso(NOW - 2 * DAY),
    assignment_count: 2,
    variables: [],
    variable_defaults: {},
  },
  {
    id: "tsk_01JWHATSAPP",
    name: "WhatsApp 未讀訊息摘要",
    goal: "打開 WhatsApp，讀取所有未讀對話的最新一則訊息，整理成清單交回。不要回覆任何訊息。",
    allowedPackages: ["com.whatsapp"],
    blockedLabelsRegex: "(?i)(傳送|send|delete|刪除)",
    allowSend: false,
    extractionSchema: null,
    schedule: { type: "INTERVAL", timesOfDay: [], daysOfWeek: [], intervalMinutes: 15 },
    visionMode: "ALWAYS",
    thinkingPolicy: "ALWAYS_ON",
    maxSteps: 60,
    runTimeoutMinutes: 20,
    webhookUrl: "https://hooks.example.com/whatsapp",
    requireUnlock: true,
    enabled: false,
    createdAt: NOW - 20 * DAY,
    updatedAt: NOW - 6 * HOUR,
    version: 9,
    created_at: iso(NOW - 20 * DAY),
    updated_at: iso(NOW - 6 * HOUR),
    assignment_count: 0,
    variables: [],
    variable_defaults: {},
  },
  {
    id: "tsk_01JSTOCK",
    name: "店舖庫存盤點",
    goal: "打開內部庫存 App，逐項記錄今日剩餘數量。",
    allowedPackages: [],
    blockedLabelsRegex: null,
    allowSend: false,
    extractionSchema: null,
    schedule: { type: "WEEKLY", timesOfDay: ["22:00"], daysOfWeek: [1, 3, 5], intervalMinutes: null },
    visionMode: "OFF",
    thinkingPolicy: "ALWAYS_OFF",
    maxSteps: 80,
    runTimeoutMinutes: 30,
    webhookUrl: null,
    requireUnlock: false,
    enabled: true,
    createdAt: NOW - 10 * DAY,
    updatedAt: NOW - 10 * DAY,
    version: 1,
    created_at: iso(NOW - 10 * DAY),
    updated_at: iso(NOW - 10 * DAY),
    assignment_count: 5,
    variables: [],
    variable_defaults: {},
  },
];

export function emptyTaskRow(id: string): TaskRow {
  return {
    id,
    name: "",
    goal: "",
    allowedPackages: [],
    blockedLabelsRegex: null,
    allowSend: false,
    extractionSchema: null,
    schedule: emptySchedule(),
    visionMode: "AUTO",
    thinkingPolicy: "ADAPTIVE",
    maxSteps: 40,
    runTimeoutMinutes: 15,
    webhookUrl: null,
    requireUnlock: true,
    enabled: true,
    createdAt: NOW,
    updatedAt: NOW,
    version: 1,
    created_at: iso(NOW),
    updated_at: iso(NOW),
    assignment_count: 0,
    variables: [],
    variable_defaults: {},
  };
}

/* ------------------------------------------------------------------ *
 * Runs
 * ------------------------------------------------------------------ */

const ERRORS = [
  "E_TIMEOUT: model did not respond within 60s",
  "E_A11Y_OFF: accessibility service disconnected",
  "E_TIMEOUT: step 14 exceeded deadline",
  "E_SCREEN_OFF: device screen could not be woken",
  "E_STALE_ELEMENT: index 3 no longer on screen",
];

function makeRun(i: number): RunRow {
  const device = MOCK_DEVICES[i % MOCK_DEVICES.length];
  const failed = i % 6 === 1;
  const running = i < 2;
  const started = NOW - i * 37 * MIN;
  const steps = running ? 6 + i : 8 + (i % 24);

  return {
    id: running ? `run_live${String(i % MOCK_DEVICES.length).padStart(3, "0")}` : `run_01JR${String(i).padStart(4, "0")}`,
    taskId: MOCK_TASKS[i % MOCK_TASKS.length].id,
    taskName: MOCK_TASKS[i % MOCK_TASKS.length].name,
    goal: MOCK_TASKS[i % MOCK_TASKS.length].goal,
    trigger: i % 3 === 0 ? "MANUAL" : "SCHEDULE",
    status: running ? "RUNNING" : failed ? "FAILED" : i % 13 === 7 ? "SKIPPED" : "SUCCESS",
    createdAt: started - 4_000,
    startedAt: started,
    finishedAt: running ? null : started + (60 + (i % 40) * 11) * 1000,
    steps,
    promptTokens: 12_400 + i * 830,
    completionTokens: 1_100 + i * 60,
    reasoningTokens: i % 2 === 0 ? 2_300 + i * 40 : 0,
    extractions: failed ? 0 : 1 + (i % 3),
    error: failed ? ERRORS[i % ERRORS.length] : null,
    summary: failed ? null : "已記錄今日最高 31°C、最低 26°C。",
    device: { id: device.id, label: device.label },
    dispatch_id: i % 5 === 0 ? "dsp_01JDISPATCH01" : null,
  };
}

export const MOCK_RUNS: RunRow[] = Array.from({ length: 64 }, (_, i) => makeRun(i));

/* ------------------------------------------------------------------ *
 * Steps
 * ------------------------------------------------------------------ */

const OBSERVATION_BASE = [
  "pkg=com.google.android.apps.weather window=Weather",
  "screen=1080x2400",
  "[0] Header 'Weather'",
  "[1] Button 'Search'",
  "[2] Item 'Hong Kong'",
  "[3] Text 'Today'",
  "[4] Text '31°'",
  "[5] Text 'Feels like 35°'",
  "[6] Item 'Hourly forecast'",
  "[7] Item '10 day forecast'",
  "[8] Text 'Humidity 78%'",
  "[9] Button 'Settings'",
];

export function mockSteps(runId: string, count: number): StepRecord[] {
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const failed = n === count && runId.includes("01JR") && count % 6 === 1;

    // Most lines repeat verbatim from the previous step — which is the whole
    // reason the observation panel has a diff toggle.
    const lines = [...OBSERVATION_BASE];
    lines[4] = `[4] Text '${29 + (n % 4)}°'`;
    if (n > 2) lines.push(`[10] Text 'Updated ${n} min ago'`);

    const tools =
      n === 1
        ? [{ tool: "open_app", args: { query: "Weather" } }]
        : n === count
          ? [{ tool: failed ? "fail" : "done", args: failed ? { reason: "找不到最低氣溫" } : { summary: "已記錄今日最高 31°C、最低 26°C。" } }]
          : n % 4 === 0
            ? [{ tool: "extract", args: { data: { temp_high: 31, temp_low: 26 } } }]
            : n % 3 === 0
              ? [{ tool: "scroll", args: { direction: "DOWN", amount: 1 } }]
              : [{ tool: "tap", args: { index: 2 + (n % 5) } }];

    const results = tools.map((t) => ({
      tool: t.tool,
      ok: !failed,
      message: failed ? "找不到符合的元素" : "ok",
      errorCode: failed ? "E_NOT_FOUND" : null,
    }));

    return {
      runId,
      n,
      observationHash: `h${n.toString(16)}`,
      observationText: lines.join("\n"),
      screenshotPath: n % 3 === 1 ? `/screens/${runId}/${n}.jpg` : null,
      thinking: n % 3 === 0,
      reasoningText:
        n % 3 === 0
          ? "畫面已顯示今日概況，但最低氣溫在下方，需要向下捲動才能讀取。"
          : null,
      toolCallsJson: JSON.stringify(tools),
      resultsJson: JSON.stringify(results),
      latencyMs: 700 + ((n * 137) % 1400),
      ttftMs: 240 + ((n * 53) % 400),
      promptTokens: 11_000 + n * 120,
      completionTokens: 90 + (n % 40),
      reasoningTokens: n % 3 === 0 ? 320 : 0,
      createdAt: NOW - (count - n) * 9_000,
    };
  });
}

export const MOCK_EXTRACTIONS: ExtractionRow[] = [
  {
    id: "ext_01JX1",
    device_id: MOCK_DEVICES[0].id,
    run_id: MOCK_RUNS[3].id,
    task_id: "tsk_01JWEATHER",
    task_name: "天氣",
    schema_version: 1,
    data: { temp_high: 31, temp_low: 26, condition: "Cloudy" },
    created_at: new Date(NOW - 3 * HOUR).toISOString(),
    received_at: new Date(NOW - 3 * HOUR).toISOString(),
    forwarded_at: null,
  },
  {
    id: "ext_01JX2",
    device_id: MOCK_DEVICES[0].id,
    run_id: MOCK_RUNS[3].id,
    task_id: "tsk_01JWEATHER",
    task_name: "天氣",
    schema_version: 1,
    // A second record with a key the first one lacks: the table generates its columns from the
    // union of what is present, and this is what keeps that honest.
    data: { temp_high: 29, temp_low: 25, condition: "Rain", warning: "黃色暴雨" },
    created_at: new Date(NOW - 4 * HOUR).toISOString(),
    received_at: new Date(NOW - 4 * HOUR).toISOString(),
    forwarded_at: null,
  },
];

/* ------------------------------------------------------------------ *
 * Overview
 * ------------------------------------------------------------------ */

export function mockOverview(): OverviewPayload {
  const online = MOCK_DEVICES.filter((d) => d.health === "online").length;
  const stale = MOCK_DEVICES.filter((d) => d.health === "stale").length;
  const offline = MOCK_DEVICES.filter((d) => d.health === "offline").length;
  const unseen = MOCK_DEVICES.filter((d) => d.health === "unseen").length;

  const running = MOCK_DEVICES.filter((d) => d.active_run).length;

  return {
    generated_at: iso(NOW),
    stats_as_of: iso(NOW - 3 * MIN),
    devices: { total: MOCK_DEVICES.length, online, stale, offline, unseen, connected: online },
    runs_in_flight: { running, waiting_unlock: 1, queued: 2, total: running + 3 },
    today: {
      runs: 142,
      success: 128,
      failed: 14,
      cancelled: 0,
      skipped: 0,
      stale: 0,
      steps: 2_713,
      extractions: 96,
      tokens: {
        prompt: 1_842_000,
        completion: 164_500,
        reasoning: 291_300,
        total: 1_842_000 + 164_500 + 291_300,
      },
      success_rate: 128 / 142,
      decided: 142,
    },
    failures_24h: [
      {
        error: "TIMEOUT",
        count: 12,
        task_id: "tsk_01JWEATHER",
        task_name: "每日天氣記錄",
        devices: 8,
        last_at: iso(NOW - 22 * MIN),
      },
      {
        error: "A11Y_OFF",
        count: 5,
        task_id: "tsk_01JSTOCK",
        task_name: "店舖庫存盤點",
        devices: 3,
        last_at: iso(NOW - 2 * HOUR),
      },
      {
        error: "SCREEN_OFF",
        count: 2,
        task_id: null,
        task_name: null,
        devices: 2,
        last_at: iso(NOW - 7 * HOUR),
      },
    ],
    quiet_devices: MOCK_DEVICES.filter((d) => d.health === "offline" || d.health === "stale")
      .slice(0, 8)
      .map((d) => ({
        id: d.id,
        label: d.label,
        model: d.model,
        health: d.health,
        last_seen_at: d.last_seen_at,
        group_id: d.group_id ?? null,
      })),
    schedule_next_hours: Array.from({ length: 6 }, (_, i) => ({
      at: iso(NOW + (i + 1) * HOUR),
      runs: [24, 3, 3, 41, 3, 3][i],
    })),
  };
}

/* ------------------------------------------------------------------ *
 * Fleet settings
 * ------------------------------------------------------------------ */

export const MOCK_FLEET_SETTINGS: FleetSettings = {
  settings: {
    baseUrl: "https://api.agens.io/v1",
    modelId: "Agens Pilot",
    defaultVisionMode: "AUTO",
    defaultThinkingPolicy: "ADAPTIVE",
    defaultMaxSteps: 40,
    compactionThresholdTokens: 300000,
    keepScreenshotsDays: 7,
    unlockGraceMinutes: 120,
    webhookUrl: "https://hooks.example.com/infocollect",
    webhookSecret: "••••••••",
  },
  managed: [
    "baseUrl",
    "modelId",
    "defaultVisionMode",
    "defaultThinkingPolicy",
    "defaultMaxSteps",
    "compactionThresholdTokens",
    "keepScreenshotsDays",
    "unlockGraceMinutes",
    "webhookUrl",
    "webhookSecret",
  ],
  max_concurrent_runs: 25,
  default_stagger_ms: 3000,
  keep_screenshots_days: 7,
  retention_days: 90,
  disk_usage_bytes: 38_654_705_664,
  disk_total_bytes: 107_374_182_400,
  agens_key_fingerprint: "9f2c41ab",
};

export const MOCK_APPS = [
  { package: "com.whatsapp", label: "WhatsApp" },
  { package: "com.android.chrome", label: "Chrome" },
  { package: "com.google.android.apps.weather", label: "Weather" },
  { package: "com.google.android.gm", label: "Gmail" },
  { package: "com.instagram.android", label: "Instagram" },
  { package: "com.example.inventory", label: "門市庫存" },
];
